import { Alert } from "@msuite/ui/alert";
import { Divider } from "@msuite/ui/divider";
import { TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceReviewFormSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ReviewCostAllocation } from "./review-cost-allocation";
import { ReviewReductionFields } from "./review-reduction-fields";
import { getReviewNetAmount } from "./review-net-amount";
import { ReviewVerdictControl } from "./review-verdict-control";

/** Props Interface */
interface IncomingInvoiceReviewFormProps {
	/** The net amount read off the invoice. */
	printedNetAmount: number | null;
	currency: string;
	/** Accepting waits on the invoice's IBAN being confirmed. */
	isAcceptBlocked: boolean;
	/** Whether the organisation keeps any active cost centre to allocate to. */
	hasCostCenters: boolean;
	/** How many of the invoice's review checks failed — accepting is allowed, knowing about them. */
	failedCheckCount: number;
}

/**
 * The review of an invoice in its approval chain: accept it with the cost allocation and, if need
 * be, a Kürzung — or reject it with a reason.
 */
export function IncomingInvoiceReviewForm({
	printedNetAmount,
	currency,
	isAcceptBlocked,
	hasCostCenters,
	failedCheckCount,
}: IncomingInvoiceReviewFormProps) {
	/** Context */
	const methods = useFormContext<IncomingInvoiceReviewFormSchema>();

	/** Hooks */
	const { t } = useTranslation();
	const [state, isReduced, reducedNetAmount] = useWatch({
		control: methods.control,
		name: ["state", "isReduced", "reducedNetAmount"],
	});

	/** Computed */
	const netAmount = getReviewNetAmount(printedNetAmount, { isReduced, reducedNetAmount });

	/** Render */
	return (
		<React.Fragment>
			<ReviewVerdictControl isAcceptBlocked={isAcceptBlocked} />
			{state === "rejected" ? (
				<TextInput
					control={methods.control}
					name="comment"
					label={t("incoming_invoices.decision.rejection_reason")}
					placeholder={t("incoming_invoices.approval_comment_placeholder")}
					isRequired
					autoFocus
					data-cy="incoming-invoice-review-comment-input"
				/>
			) : (
				<React.Fragment>
					{failedCheckCount > 0 && (
						<Alert
							variant="warning"
							title={t("incoming_invoices.failed_checks_title")}
							description={t("incoming_invoices.failed_checks_description", {
								count: `${failedCheckCount}`,
							})}
						/>
					)}
					<ReviewReductionFields
						printedNetAmount={printedNetAmount}
						currency={currency}
					/>
					<Divider variant="modal" />
					<ReviewCostAllocation
						netAmount={netAmount}
						currency={currency}
						hasCostCenters={hasCostCenters}
					/>
					<Divider variant="modal" />
					<TextInput
						control={methods.control}
						name="comment"
						label={t("incoming_invoices.decision.comment")}
						placeholder={t("incoming_invoices.decision.comment_placeholder")}
						isRequired={false}
						data-cy="incoming-invoice-review-comment-input"
					/>
				</React.Fragment>
			)}
		</React.Fragment>
	);
}
